import { Trans } from '@lingui/react/macro'
import { useMemoizedFn } from 'ahooks'

import {
  TTS_FORM_VOICE_DISPLAY_COUNT,
  TTS_MAX_DIALOGUES,
  TTS_MAX_TEXT_LENGTH,
} from '../../_constant'
import { TTS_FORM_DEFAULTS } from '../../_store'
import { useTTSStore } from '../../_store/useTTSStore'

import DialogueCard from './DialogueCard'
import VoiceModal from './VoiceModal'
import TTSAdvancedSettings from './TTSAdvancedSettings'
import { useTTSSubmit } from './_hooks/useTTSSubmit'
import { useVoices } from './_hooks/useVoices'
import { useVoicePlayer } from './_hooks/useVoicePlayer'

import type { DialogueItem, TTSFormValues, VoiceItem } from '../../_types/form'

import CreateButton from '@/pages/tad.ai/_components/CreateButton'
import ScrollArea from '@/pages/tad.ai/_components/ScrollArea'
import { useScreen } from '@/hooks/screen'
import StickyFormActionLayout from '@/pages/tad.ai/_components/StickyFormActionLayout'


import { useState, useRef, useEffect, useMemo } from 'react';
import { cls } from 'tagged-classnames-free';
interface TTSFormProps {
  onCreated?: () => void
}

function TTSForm(props: TTSFormProps) {
  const { onCreated } = props
  const isXl = useScreen('xl')

  // ─── Store 状态订阅 ───
  const formValues = useTTSStore((s) => s.form)
  const hasHydrated = useTTSStore((s) => s._hasHydrated)
  const isSubmitting = useTTSStore((s) => s.ui.isSubmitting)
  const advancedExpanded = useTTSStore((s) => s.prefs.advancedExpanded)
  const setFormValues = useTTSStore((s) => s.setFormValues)
  const resetForm = useTTSStore((s) => s.resetForm)
  const setPrefs = useTTSStore((s) => s.setPrefs)

  const { handleSubmit } = useTTSSubmit({ onSuccess: onCreated })
  const {
    categories,
    allVoices,
    isLoading: voicesLoading,
    searchVoices,
  } = useVoices()
  const voicePlayer = useVoicePlayer()

  const [voiceModalOpen, setVoiceModalOpen] = useState(false)
  const [activeDialogueIndex, setActiveDialogueIndex] = useState(0)

  // SSR-safe: hydrate 完成前用静态默认值
  const displayValues = hasHydrated ? formValues : TTS_FORM_DEFAULTS

  // Per-dialogue display voice lists, keyed by dialogue index
  const [displayVoicesMap, setDisplayVoicesMap] = useState<
    Record<number, VoiceItem[]>
  >({})

  // Initialize display voices from store: pin selected voices outside default 6
  // and default empty voices to the first available voice
  const draftInitializedRef = useRef(false)
  useEffect(() => {
    if (draftInitializedRef.current || !hasHydrated || !allVoices.length) return
    draftInitializedRef.current = true

    const dialogue = formValues.dialogue
    const first6 = allVoices.slice(0, TTS_FORM_VOICE_DISPLAY_COUNT)
    const map: Record<number, VoiceItem[]> = {}
    const defaultVoiceId = allVoices[0]?.id ?? ''

    // Auto-fill empty voices with the first available voice
    const hasEmptyVoice = dialogue.some((d) => !d.voice)
    if (hasEmptyVoice && defaultVoiceId) {
      const updatedDialogue = dialogue.map((d) =>
        d.voice ? d : { ...d, voice: defaultVoiceId },
      )
      setFormValues({ dialogue: updatedDialogue })
    }

    dialogue.forEach((d, i) => {
      if (!d.voice || first6.some((v) => v.id === d.voice)) return
      const voice = allVoices.find((v) => v.id === d.voice)
      if (voice) {
        map[i] = [voice, ...first6.slice(0, TTS_FORM_VOICE_DISPLAY_COUNT - 1)]
      }
    })
    if (Object.keys(map).length) setDisplayVoicesMap(map)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasHydrated, allVoices])

  const getDisplayVoices = (dialogueIndex: number): VoiceItem[] => {
    return (
      displayVoicesMap[dialogueIndex] ??
      allVoices.slice(0, TTS_FORM_VOICE_DISPLAY_COUNT)
    )
  }

  const pinVoiceToDisplay = (dialogueIndex: number, voice: VoiceItem) => {
    const current = getDisplayVoices(dialogueIndex)
    if (current.some((v) => v.id === voice.id)) return

    setDisplayVoicesMap((prev) => ({
      ...prev,
      [dialogueIndex]: [
        voice,
        ...current.slice(0, TTS_FORM_VOICE_DISPLAY_COUNT - 1),
      ],
    }))
  }

  const updateFormValues = useMemoizedFn((updates: Partial<TTSFormValues>) => {
    setFormValues(updates)
  })

  const updateDialogue = useMemoizedFn(
    (index: number, updates: Partial<DialogueItem>) => {
      const newDialogue = [...displayValues.dialogue]
      newDialogue[index] = { ...newDialogue[index], ...updates }
      updateFormValues({ dialogue: newDialogue })
    },
  )

  const addDialogue = useMemoizedFn(() => {
    if (displayValues.dialogue.length >= TTS_MAX_DIALOGUES) return
    const defaultVoice = allVoices[0]?.id ?? ''
    updateFormValues({
      dialogue: [...displayValues.dialogue, { text: '', voice: defaultVoice }],
    })
  })

  const removeDialogue = useMemoizedFn((index: number) => {
    if (displayValues.dialogue.length <= 1) return
    if (voicePlayer.currentSourceId === `grid-${index}`) {
      voicePlayer.stop()
    }
    const newDialogue = displayValues.dialogue.filter((_, i) => i !== index)
    updateFormValues({ dialogue: newDialogue })
    if (activeDialogueIndex >= newDialogue.length) {
      setActiveDialogueIndex(Math.max(0, newDialogue.length - 1))
    }
  })

  const handleVoiceModalSelect = useMemoizedFn((voice: VoiceItem) => {
    pinVoiceToDisplay(activeDialogueIndex, voice)
    updateDialogue(activeDialogueIndex, { voice: voice.id })
    setVoiceModalOpen(false)
    voicePlayer.stop()
  })

  const handleViewMore = useMemoizedFn((index: number) => {
    setActiveDialogueIndex(index)
    setVoiceModalOpen(true)
  })

  const onSubmit = useMemoizedFn(async () => {
    await handleSubmit(displayValues)
  })

  const totalChars = useMemo(
    () => displayValues.dialogue.reduce((sum, d) => sum + d.text.length, 0),
    [displayValues.dialogue],
  )
  const isTotalCharsError = totalChars > TTS_MAX_TEXT_LENGTH
  const isTotalCharsAtLimit = totalChars >= TTS_MAX_TEXT_LENGTH

  const canAddDialogue =
    displayValues.dialogue.length < TTS_MAX_DIALOGUES &&
    displayValues.dialogue.every((d) => d.text.trim() && d.voice) &&
    !isTotalCharsAtLimit

  return (
    <div
      className={cls`bg-f-other-1 scrollbar z-0 mb-6 flex-col overflow-x-hidden rounded-2xl xl:w-[680px]`}
    >
      <div
        className={cls`
          scrollbar-none mx-4 flex h-full flex-col rounded-2xl
          md:mx-8
          xl:max-w-none
          xl:overflow-auto
        `}
      >
        <ScrollArea
          disabled={!isXl}
          className={cls`
            bg-f-bg-elevated flex-1 overflow-hidden rounded-2xl
          `}
        >
          <StickyFormActionLayout
            className={cls`pt-5`}
            contentClassName={cls`relative z-0 px-4 md:px-6`}
            actionClassName={cls`px-4 md:px-6`}
            action={
              <CreateButton
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={onSubmit}
              />
            }
          >
            <div className={cls`flex flex-col gap-6 pb-4`}>
              {/* Dialogue Header */}
              <div className={cls`border-f-border border-b pb-6`}>
                <div className={cls`flex items-center justify-between`}>
                  <span className={cls`text-f-text text-xl font-semibold`}>
                    <Trans>Dialogue</Trans>
                  </span>
                  <span className={cls`text-f-text-quaternary text-sm`}>
                    <span className={isTotalCharsError ? 'text-f-error' : ''}>
                      {totalChars}
                    </span>
                    /{TTS_MAX_TEXT_LENGTH}
                  </span>
                </div>
              </div>

              {/* Dialogue Cards */}
              {displayValues.dialogue.map((dialogue, index) => (
                <DialogueCard
                  key={index}
                  index={index}
                  text={dialogue.text}
                  voiceId={dialogue.voice}
                  displayVoices={getDisplayVoices(index)}
                  canDelete={displayValues.dialogue.length > 1}
                  voicesLoading={voicesLoading}
                  currentPlayingId={voicePlayer.currentVoiceId}
                  currentSourceId={voicePlayer.currentSourceId}
                  isPlaying={voicePlayer.isPlaying}
                  playLoading={voicePlayer.loading}
                  onTextChange={(text) => updateDialogue(index, { text })}
                  onVoiceSelect={(voice) =>
                    updateDialogue(index, { voice: voice.id })
                  }
                  onDelete={() => removeDialogue(index)}
                  onViewMore={() => handleViewMore(index)}
                  onTogglePlay={(voice) =>
                    voicePlayer.play(
                      voice.id,
                      voice.previewUrl,
                      `grid-${index}`,
                    )
                  }
                />
              ))}

              {/* Add Dialogue Button */}
              {displayValues.dialogue.length < TTS_MAX_DIALOGUES && (
                <button
                  type='button'
                  disabled={!canAddDialogue}
                  onClick={addDialogue}
                  className={cls`
                    flex w-full items-center justify-center rounded-lg px-3 py-[10px] text-lg font-semibold transition-colors
                    ${
                      canAddDialogue
                        ? 'text-f-text-tertiary hover:text-f-text-invert hover:bg-f-text bg-f-bg-base cursor-pointer'
                        : 'text-f-text-disabled bg-f-bg-container cursor-not-allowed'
                    }
                  `}
                >
                  <Trans>Add Dialogue</Trans>
                </button>
              )}

              {/* Advanced Settings */}
              <TTSAdvancedSettings
                stability={displayValues.stability}
                speechLanguage={displayValues.speech_language}
                onStabilityChange={(value) =>
                  updateFormValues({ stability: value })
                }
                onSpeechLanguageChange={(value) =>
                  updateFormValues({ speech_language: value })
                }
                expanded={advancedExpanded}
                onExpandedChange={(expanded) =>
                  setPrefs({ advancedExpanded: expanded })
                }
              />
            </div>
          </StickyFormActionLayout>
        </ScrollArea>
      </div>

      {/* Voice Modal */}
      <VoiceModal
        open={voiceModalOpen}
        onClose={() => {
          setVoiceModalOpen(false)
          voicePlayer.stop()
        }}
        categories={categories}
        isLoading={voicesLoading}
        selectedVoiceId={
          displayValues.dialogue[activeDialogueIndex]?.voice ?? ''
        }
        onSelect={handleVoiceModalSelect}
        searchVoices={searchVoices}
        currentPlayingId={voicePlayer.currentVoiceId}
        currentSourceId={voicePlayer.currentSourceId}
        isPlaying={voicePlayer.isPlaying}
        playLoading={voicePlayer.loading}
        onTogglePlay={(voice) =>
          voicePlayer.play(voice.id, voice.previewUrl, 'modal')
        }
      />
    </div>
  )
}

export default TTSForm
